import { useCreateCollectionAndRedirect } from '@frameio/collections/operations/contexts';
import { useProjectWorkspaceInfoFragment } from '@frameio/graphql-client/generated';
import type { useMemberAccessFlow } from '@frameio/invites/hooks';
import { useToggleProjectNotifications } from '@frameio/notifications/hooks';
import {
  useProjectPermissions,
  useWorkspacePermissions,
} from '@frameio/permissions';
import { useActiveProjectId } from '@frameio/shared/contexts';
import { useIsMatchingClientName } from '@frameio/shared/contexts/ClientNameContext';
import { OpenOnDesktopMenuButton } from '@frameio/shared/resources/mounted-storage';
import { useCopyLink } from '@frameio/shared/routing';
import type { TrackingSourceType } from '@frameio/shared/segment/hooks/utils/trackingUtils';
import { MenuButton, MenuGroup, MenuSwitch } from '@frameio/vapor';
import {
  IconArchive,
  IconCog,
  IconCollectionsAdd,
  IconDuplicate,
  IconFolderAdd,
  IconLink,
  IconNotifications,
  IconPeople,
  IconTrash,
  IconUnarchived,
} from '@frameio/vapor-icons';
import { useProjectStatusToggle } from '@frameio/workspaces/shared/hooks';
import { CreateFolderButton } from './CreateFolderButton';
import TEXT from './generated/TEXT/ProjectMenu';

type Props = {
  onDeletePress: () => void;
  onDuplicatePress: () => void;
  onProjectSettingsPress: () => void;
  rootFolderId: string;
} & Pick<ReturnType<typeof useMemberAccessFlow>, 'renderMemberAccessButton'>;

export function ProjectMenu({
  onDeletePress,
  onDuplicatePress,
  onProjectSettingsPress,
  renderMemberAccessButton,
  rootFolderId,
}: Props) {
  const projectId = useActiveProjectId() ?? '';
  const isWebClient = useIsMatchingClientName('web');

  const { data: project } = useProjectWorkspaceInfoFragment(projectId);
  const isInactive = Boolean(project?.archivedAt);
  const isWorkfront = project?.isWorkfront;

  const canCreate = useWorkspacePermissions({
    check: 'canCreateProject',
    workspaceId: project?.workspace?.id ?? '',
  });

  const hasDeletePermission = useProjectPermissions({
    check: 'canDelete',
    projectId,
  });

  const canUpdate = useProjectPermissions({
    check: 'canUpdate',
    projectId,
  });

  const canEditUserPermissions = useProjectPermissions({
    check: 'canEditUserPermissions',
    projectId,
  });

  const canCreateTeamCollection = useProjectPermissions({
    check: 'canCreateCollection',
    projectId,
  });

  const canDelete = hasDeletePermission && !isWorkfront;
  const canDuplicate = canCreate && !isInactive && !isWorkfront;

  const { isLoading: isCreatingCollection, createCollectionAndRedirect } =
    useCreateCollectionAndRedirect();

  const { toggleProjectNotificationsMutation: toggleProjectNotifications } =
    useToggleProjectNotifications({
      projectId: project?.id,
    });

  const copyLink = useCopyLink();

  const { handleStatusToggleClick } = useProjectStatusToggle({
    archivedAt: project.archivedAt,
    id: project.id ?? '',
    name: project.name ?? '',
    refetch: true,
    trackingSource: TRACKING_SOURCE,
    workspaceId: project.workspace?.id ?? '',
  });

  const handleCreateCollectionPress = () => {
    if (isCreatingCollection) return;

    createCollectionAndRedirect({
      projectId,
      private: !canCreateTeamCollection,
    });
  };

  const handleNotificationsPress = () => {
    toggleProjectNotifications();
  };

  const handleCopyLink = () => {
    copyLink({
      page: 'folder',
      projectId,
      folderAssetId: undefined, // undefined for root folder
    });
  };

  return (
    <>
      <MenuGroup>
        <MenuButton iconBefore={<IconLink />} onPress={handleCopyLink}>
          {TEXT.copyProjectUrl}
        </MenuButton>

        {isWebClient &&
          canEditUserPermissions &&
          renderMemberAccessButton(({ onClick, ...bind }) => (
            <MenuButton
              {...bind}
              iconBefore={<IconPeople />}
              label={TEXT.manageMembers}
              onPress={() => onClick?.('members')}
            >
              {TEXT.manageMembers}
            </MenuButton>
          ))}

        <OpenOnDesktopMenuButton
          projectId={projectId}
          trackingSource={TRACKING_SOURCE}
        />
      </MenuGroup>

      <MenuGroup>
        <MenuButton
          iconBefore={<IconFolderAdd />}
          label={TEXT.newFolder}
          submenu={
            <MenuGroup>
              <CreateFolderButton
                parentId={rootFolderId}
                projectId={projectId}
                trackingSource={TRACKING_SOURCE}
                variant="menu-button"
              />

              <CreateFolderButton
                parentId={rootFolderId}
                projectId={projectId}
                restricted={true}
                trackingSource={TRACKING_SOURCE}
                variant="menu-button"
              />
            </MenuGroup>
          }
        >
          {TEXT.newFolder}
        </MenuButton>

        <MenuButton
          iconBefore={<IconCollectionsAdd />}
          label={TEXT.newCollection}
          onPress={handleCreateCollectionPress}
        >
          {TEXT.newCollection}
        </MenuButton>
      </MenuGroup>

      <MenuGroup>
        <MenuSwitch
          checked={project.notificationsEnabled ?? false}
          iconBefore={<IconNotifications />}
          label={TEXT.notifications}
          name={TEXT.notifications}
          onPress={handleNotificationsPress}
          shouldCloseMenuOnSelect={true}
        >
          {TEXT.notifications}
        </MenuSwitch>

        {!isInactive && (
          <MenuButton
            disabled={!canDuplicate}
            iconBefore={<IconDuplicate />}
            label={TEXT.duplicate}
            onPress={onDuplicatePress}
          >
            {TEXT.duplicate}
          </MenuButton>
        )}

        <MenuButton
          disabled={!canUpdate}
          iconBefore={isInactive ? <IconUnarchived /> : <IconArchive />}
          label={isInactive ? TEXT.markActive : TEXT.markInactive}
          onPress={handleStatusToggleClick}
        >
          {isInactive ? TEXT.markActive : TEXT.markInactive}
        </MenuButton>

        <MenuButton
          iconBefore={<IconCog />}
          label={TEXT.projectSettings}
          onPress={onProjectSettingsPress}
        >
          {TEXT.projectSettings}
        </MenuButton>
      </MenuGroup>

      {!isWorkfront && (
        <MenuGroup>
          <MenuButton
            disabled={!canDelete}
            iconBefore={<IconTrash />}
            label={TEXT.delete}
            onPress={onDeletePress}
            variant="destructive"
          >
            {TEXT.delete}
          </MenuButton>
        </MenuGroup>
      )}
    </>
  );
}

const TRACKING_SOURCE: TrackingSourceType = 'ProjectNavProjectMenu';

TEXT.DEFINE({
  /** Label for the option to copy the current project's URL in the Project Menu */
  copyProjectUrl: 'Copy Project URL',
  /** Label for the delete option for Project Menu */
  delete: 'Delete',
  /** Label for the duplicate option for Project Menu */
  duplicate: 'Duplicate',
  /** Label for the manage members option for Project Menu */
  manageMembers: 'Manage Members',
  /** Label for the option to mark a project as active in the Project Menu */
  markActive: 'Mark Active',
  /** Label for the option to mark a project as inactive in the Project Menu */
  markInactive: 'Mark Inactive',
  /** Label for the option to create a new collection in the Project Menu */
  newCollection: 'New Collection',
  /** Label for the option to create a new folder in the Project Menu */
  newFolder: 'New Folder',
  /** Label for the option to create a new folder in the Project Menu */
  newFolderAriaLabel: 'New Folder submenu',
  /** Label for the option to toggle notifications in the Project Menu */
  notifications: 'Notifications',
  /** Label for the project settings option for Project Menu */
  projectSettings: 'Project Settings',
});
